/*
 * gezensepet motion — tiny, dependency-free (≈2 KB).
 * - Scroll reveals via a single shared IntersectionObserver
 * - Word-split headlines
 * - Pointer parallax + card tilt on fine pointers only
 * - Pauses off-screen marquees and loops to save battery
 * Everything degrades to a static, fully visible page.
 */
(() => {
  const root = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const saveData = navigator.connection && navigator.connection.saveData;

  root.classList.add('gs-js');

  /* ---------- Word split ---------- */
  const splitWords = (el) => {
    if (el.dataset.gsSplitDone) return;
    const text = el.textContent.trim();
    const words = text.split(/\s+/);
    el.textContent = '';
    const sr = document.createElement('span');
    sr.className = 'gs-visually-hidden';
    sr.textContent = text;
    el.appendChild(sr);
    words.forEach((word, i) => {
      const outer = document.createElement('span');
      outer.className = 'gs-word';
      outer.setAttribute('aria-hidden', 'true');
      outer.style.setProperty('--gs-w', i);
      const inner = document.createElement('span');
      inner.textContent = word;
      outer.appendChild(inner);
      el.appendChild(outer);
      if (i < words.length - 1) el.appendChild(document.createTextNode(' '));
    });
    el.dataset.gsSplitDone = 'true';
  };

  /* ---------- Reveal observer ---------- */
  const revealIO =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              entry.target.classList.add('is-in');
              revealIO.unobserve(entry.target);
            });
          },
          { rootMargin: "0px 0px -6% 0px", threshold: 0 }
        )
      : null;

  /* ---------- Pause loops when off-screen ---------- */
  const loopIO =
    'IntersectionObserver' in window
      ? new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            entry.target.classList.toggle('is-paused', !entry.isIntersecting);
          });
        })
      : null;

  /* ---------- Pointer parallax (hero) ---------- */
  const bindParallax = (scene) => {
    if (scene.dataset.gsParallaxBound) return;
    scene.dataset.gsParallaxBound = 'true';
    const layers = scene.querySelectorAll('[data-gs-depth]');
    let frame = null;
    let px = 0;
    let py = 0;

    const apply = () => {
      frame = null;
      layers.forEach((layer) => {
        const depth = parseFloat(layer.dataset.gsDepth) || 0;
        layer.style.translate = `${px * depth}px ${py * depth}px`;
      });
    };

    scene.addEventListener(
      'pointermove',
      (event) => {
        if (reduceMotion.matches || !finePointer.matches) return;
        const rect = scene.getBoundingClientRect();
        px = (event.clientX - rect.left) / rect.width - 0.5;
        py = (event.clientY - rect.top) / rect.height - 0.5;
        if (!frame) frame = requestAnimationFrame(apply);
      },
      { passive: true }
    );
    scene.addEventListener('pointerleave', () => {
      px = 0;
      py = 0;
      if (!frame) frame = requestAnimationFrame(apply);
    });
  };

  /* ---------- Tilt (cards) ---------- */
  const bindTilt = (el) => {
    if (el.dataset.gsTiltBound) return;
    el.dataset.gsTiltBound = 'true';
    const max = parseFloat(el.dataset.gsTilt) || 6;
    let frame = null;
    let rx = 0;
    let ry = 0;

    const apply = () => {
      frame = null;
      el.style.setProperty('--gs-rx', `${rx}deg`);
      el.style.setProperty('--gs-ry', `${ry}deg`);
    };

    el.addEventListener(
      'pointermove',
      (event) => {
        if (reduceMotion.matches || !finePointer.matches) return;
        const rect = el.getBoundingClientRect();
        ry = ((event.clientX - rect.left) / rect.width - 0.5) * max * 2;
        rx = -((event.clientY - rect.top) / rect.height - 0.5) * max * 2;
        if (!frame) frame = requestAnimationFrame(apply);
      },
      { passive: true }
    );
    el.addEventListener('pointerleave', () => {
      rx = 0;
      ry = 0;
      if (!frame) frame = requestAnimationFrame(apply);
    });
  };

  /* ---------- Init (also re-run inside the theme editor) ---------- */
  const init = (scope = document) => {
    scope.querySelectorAll('[data-gs-split]').forEach((el) => {
      splitWords(el);
      el.classList.add('gs-split');
      if (revealIO && !reduceMotion.matches) revealIO.observe(el);
      else el.classList.add('is-in');
    });

    scope.querySelectorAll('[data-gs-stagger]').forEach((group) => {
      [...group.children].forEach((child, i) => {
        if (!child.hasAttribute('data-gs-reveal')) child.setAttribute('data-gs-reveal', '');
        child.style.setProperty('--gs-i', i);
      });
    });

    scope.querySelectorAll('[data-gs-reveal]').forEach((el) => {
      if (revealIO && !reduceMotion.matches) revealIO.observe(el);
      else el.classList.add('is-in');
    });

    scope.querySelectorAll('.gs-marquee, [data-gs-loop]').forEach((el) => {
      if (loopIO) loopIO.observe(el);
      if (saveData) el.classList.add('is-paused');
    });

    scope.querySelectorAll('[data-gs-parallax]').forEach(bindParallax);
    scope.querySelectorAll('[data-gs-tilt]').forEach(bindTilt);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));

  window.gezensepet = Object.assign(window.gezensepet || {}, { initMotion: init });
})();
